/**
 * Vulnerability Share Handlers Hook
 *
 * Provides handler for sharing vulnerability URLs via clipboard.
 * Follows the pattern established in useAssetActionHandlers.
 *
 * @module sections/vulnerabilities/hooks/actions
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { VulnerabilityShareHandlers } from '@/sections/vulnerabilities/types/action.types';
import { Risk } from '@/types';

/**
 * Hook for vulnerability share operations
 *
 * Generates shareable URLs with drawer state for direct navigation
 * to vulnerability details. Copies URLs to clipboard with user feedback.
 *
 * @param selectedVulnerabilities - Currently selected vulnerabilities
 * @returns Share handler functions
 *
 * @example
 * ```tsx
 * const { handleShare } = useVulnerabilityShareHandlers(selectedVulnerabilities);
 *
 * return (
 *   <ShareButton onClick={handleShare} />
 * );
 * ```
 */
export const useVulnerabilityShareHandlers = (
  selectedVulnerabilities: Risk[]
): VulnerabilityShareHandlers => {
  /**
   * Shares vulnerability URLs by copying them to clipboard
   * Generates deep links with drawer state for direct access
   *
   * URL format: {origin}{pathname}?vulnerabilityDrawerKey={key}&drawerOrder=["vulnerability"]
   */
  const handleShare = useCallback(() => {
    try {
      const urls = selectedVulnerabilities.map(vulnerability => {
        // Extract base path up to userId (e.g., "/encoded-user-id")
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const basePath = pathParts[0] ? `/${pathParts[0]}` : '';

        const params = new URLSearchParams();
        params.append('vulnerabilityDrawerKey', vulnerability.key);
        params.append('drawerOrder', JSON.stringify(['vulnerability']));
        return `${window.location.origin}${basePath}/vulnerabilities?${params.toString()}`;
      });

      navigator.clipboard.writeText(urls.join('\n'));
      toast.success(
        `Copied ${urls.length} vulnerability URL${urls.length > 1 ? 's' : ''} to clipboard`
      );
    } catch (error) {
      toast.error('Failed to copy URLs to clipboard');
      console.error('[useVulnerabilityShareHandlers] Failed to share:', error);
    }
  }, [selectedVulnerabilities]);

  return {
    handleShare,
  };
};
